import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Building2, 
  Layers, 
  Wrench, 
  LayoutGrid, 
  Ruler,
  CheckCircle2,
  ArrowRight,
  Phone
} from "lucide-react";

const services = [
  {
    icon: Building2,
    title: "Trockenbau",
    description: "Professionelle Trockenbauarbeiten für Wand- und Deckenkonstruktionen mit höchster Präzision. Wir realisieren Ihre Projekte termingerecht und in bester Qualität.",
    features: [
      "Trennwände und Vorsatzschalen",
      "Abhangdecken",
      "Dachgeschossausbau",
      "Brandschutzverkleidungen",
      "Schallschutz"
    ],
    image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&q=80",
    color: "bg-amber-500"
  },
  {
    icon: Layers,
    title: "Bodenbeläge",
    description: "Fachgerechte Verlegung verschiedenster Bodenbeläge. Wir beraten Sie bei der Auswahl und sorgen für eine perfekte Verarbeitung.",
    features: [
      "Laminat",
      "Vinyl & Designbeläge",
      "Parkett",
      "Teppichboden",
      "Linoleum"
    ],
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80",
    color: "bg-amber-500"
  },
  {
    icon: Wrench,
    title: "Montagearbeiten",
    description: "Montage von Türen, Fenstern und sonstigen Bauelementen. Präzise Arbeit für ein perfektes Endergebnis.",
    features: [
      "Innentüren",
      "Zargen",
      "Sockelleisten",
      "Fensterbänke",
      "Schiebetüren"
    ],
    image: "https://images.unsplash.com/photo-1558618047-f4b511b41c99?w=600&q=80",
    color: "bg-amber-500"
  },
  {
    icon: LayoutGrid,
    title: "Deckensysteme",
    description: "Installation moderner Deckensysteme für optimale Raumakustik und Ästhetik. Von der Planung bis zur Umsetzung.",
    features: [
      "Abgehängte Decken",
      "Akustikdecken",
      "Spanndecken",
      "LED-Integration",
      "Designdecken"
    ],
    image: "https://images.unsplash.com/photo-1560749003-f4b1e17e2dff?w=600&q=80",
    color: "bg-amber-500"
  },
  {
    icon: Ruler,
    title: "Trockenstrich",
    description: "Schnelle und saubere Trockenstrich-Lösungen für optimale Bodenvorbereitung. Ideal für Renovierung und Neubau.",
    features: [
      "Trockenestrich",
      "Ausgleichsschüttung",
      "Wärmedämmung",
      "Trittschalldämmung",
      "Heizungsinstallation"
    ],
    image: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=600&q=80",
    color: "bg-amber-500"
  }
];

const LeistungenPage = () => {
  return (
    <div data-testid="leistungen-page" style={{ backgroundColor: '#372d24' }}>
      {/* Hero Section */}
      <section className="pt-12 pb-6" style={{ backgroundColor: '#372d24' }} data-testid="leistungen-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif', color: '#dbcebe' }}>
              Professioneller Innenausbau aus einer Hand
            </h1>
            <p className="text-lg leading-relaxed" style={{ color: '#dbcebe' }}>
              Von Trockenbau über Bodenbeläge bis hin zu Deckensystemen – wir bieten Ihnen ein umfassendes Leistungsspektrum für Ihren Innenausbau. Qualität und Präzision stehen bei uns an erster Stelle.
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-8" style={{ backgroundColor: '#372d24' }} data-testid="leistungen-detail">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => (
              <div 
                key={service.title}
                className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-start ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`service-detail-${index}`}
              >
                {/* Image */}
                <div className={`relative ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="rounded-2xl overflow-hidden shadow-xl image-zoom border border-zinc-800 mb-6">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-[320px] object-cover"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className={`space-y-6 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <h2 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#dbcebe' }}>
                    {service.title}
                  </h2>
                  <p className="leading-relaxed" style={{ color: '#a89f94' }}>
                    {service.description}
                  </p>
                  <ul className="space-y-3">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-3">
                        <CheckCircle2 className="h-5 w-5 text-amber-400 flex-shrink-0" />
                        <span style={{ color: '#dbcebe' }}>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-amber-500" data-testid="leistungen-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Haben Sie Fragen zu unseren Leistungen?
          </h2>
          <p className="text-amber-900 text-lg mb-8 max-w-2xl mx-auto">
            Kontaktieren Sie uns für eine unverbindliche Beratung. Wir freuen uns auf Ihr Projekt!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/kontakt">
              <Button 
                size="lg" 
                className="bg-black hover:bg-zinc-900 text-white px-8"
                data-testid="leistungen-kontakt-btn"
              >
                Kontakt aufnehmen
              </Button>
            </Link>
            <a href="tel:+491621608568">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-black text-black hover:bg-black/10 px-8"
                data-testid="leistungen-phone-btn"
              >
                <Phone className="mr-2 h-5 w-5" />
                +49 162 1608568
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LeistungenPage;
